import { createSignal, Show } from "solid-js";

function copyText(text: string) {
  navigator.clipboard.writeText(text).catch(() => {});
}

export function ExpandablePayload(props: { payload: unknown }) {
  const [expanded, setExpanded] = createSignal(false);
  const json = () => JSON.stringify(props.payload);
  const truncated = () => json().slice(0, 80);

  return (
    <span
      onClick={() => setExpanded((e) => !e)}
      class="cursor-pointer hover:text-gray-200 inline-block max-w-full"
      title="Click to expand/collapse"
    >
      <Show
        when={expanded() || json().length <= 80}
        fallback={<span class="text-gray-400">{truncated()}...</span>}
      >
        <span class="text-gray-300 break-all">{json()}</span>
      </Show>
    </span>
  );
}

export function DirectionIndicator(props: { channels?: string[] }) {
  const ch = props.channels ?? [];
  const dir = () => {
    if (ch.length === 0) return { label: "-> *", color: "text-gray-600" };
    const targets: string[] = [];
    for (const c of ch) {
      if (c === "command") targets.push("-> cmd");
      else if (c.startsWith("searcher:")) targets.push("-> src");
      else if (c.startsWith("sos:")) targets.push("-> sos");
      else targets.push(c === "*" ? "-> *" : `-> ${c}`);
    }
    return { label: targets.slice(0, 3).join(" "), color: "text-gray-500" };
  };

  return <span class={`${dir().color} text-[10px] whitespace-nowrap`}>{dir().label}</span>;
}

export function RoutingDecisionIndicator(props: { routing?: any; channels?: string[] }) {
  const routing = props.routing;
  if (!routing) {
    return <DirectionIndicator channels={props.channels} />;
  }

  const status = routing.status ?? "unknown";
  const attempted = Array.isArray(routing.attempted) ? routing.attempted : [];
  const label = attempted.length > 0
    ? attempted
        .map((a: any) => `${a.channel}${typeof a.delivered === "number" ? `(${a.delivered})` : ""}`)
        .slice(0, 3)
        .join(" ")
    : (props.channels ?? []).join(" ");

  const color =
    status === "broadcast" ? "text-cyan-400" :
    status === "sent" ? "text-green-400" :
    status === "blocked" ? "text-red-400" :
    status === "fallback" ? "text-amber-400" :
    "text-gray-500";

  return (
    <span
      class={`${color} text-[10px] whitespace-nowrap`}
      title={routing.blockedReasons?.join(", ") || status}
    >
      {status}: {label || "none"}
    </span>
  );
}

export function CopyButton(props: { text: string }) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        copyText(props.text);
      }}
      class="text-gray-600 hover:text-gray-300 cursor-pointer"
      title="Copy payload"
    >
      Copy
    </button>
  );
}
